import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useProducts } from '@/features/product/hooks';
import { getAgentRun } from '@/features/predicate/api';
import type { AgentRunDetail } from '@/features/predicate/api';
import { getDocument } from '@/features/vault/api';
import type { VaultDocumentDetail } from '@/features/vault/types';
import { getCompany } from '@/features/customer/api';
import type { CompanyDto } from '@/features/customer/api';
import { companyName } from '@/features/customer/company-data';
import { navItemForPath } from './nav-items';
import { decodeProductId } from '@/lib/product-url-codec';

/**
 * BreadcrumbBar — the slim white strip where the Subnav tabs used to live
 * (sidebar prototype). Shows `Dashboard › <Section> › <Detail…>` derived
 * from the current pathname via the shared nav-items lookup, so labels —
 * and now ICONS — have one home with the sidebar: each linked crumb
 * carries its section's sidebar icon, visually tying the trail to the
 * rail. The current page renders as a soft navy pill so "where am I"
 * reads at a glance.
 *
 * Detail crumbs are best-effort prettification: numeric segments (every
 * numeric param in the app is a product id) resolve to the product NAME via
 * the ['products'] cache — never the raw global id, which would leak
 * cross-tenant volume — and kebab segments title-case ("new" → "New").
 */
interface Crumb {
  label: string;
  to: string | null; // null = not a link (current page, or no such route)
  icon?: LucideIcon;
}

/** Routes that live OUTSIDE the sidebar nav but belong to a section —
 *  the agent result pages. Mapped explicitly so the trail keeps the
 *  section link instead of flattening to one giant unlinked crumb. */
const RESULT_ROUTES: Record<string, { section: string; leaf: string }> = {
  '/predicate-finder-result': { section: '/predicate-finder', leaf: 'Run Result' },
};

/** Avatar-menu destinations — labels match what the menu calls them. */
const MENU_ROUTE_LABELS: Record<string, string> = {
  '/profile': 'Profile & Settings',
  '/team': 'Team & Invites',
  '/faq': 'FAQ',
  '/support': 'Help & Support',
};

/** Intermediate path segments that are REAL navigable pages — only these
 *  render as links mid-trail. Everything else (e.g. the "Product #12" in
 *  /product-account/12/edit, which has no standalone page) stays text. */
const LINKABLE_SUBPATHS = new Set([
  '/customer-account/new',
  '/product-account/new',
]);

/** Friendlier names for known structural segments. */
const SEGMENT_LABELS: Record<string, string> = {
  form: 'Wizard Form',
};

function prettify(segment: string, productNames?: Map<number, string>): string {
  // Numeric segments are always a product id (every numeric param in the app
  // is one). Show the product NAME, never the raw global id — that leaks
  // cross-tenant volume. Unknown / not-yet-loaded → neutral "Product".
  if (/^\d+$/.test(segment)) return productNames?.get(Number(segment)) ?? 'Product';
  // The opaque product token the URL now carries (`p…`, see run-url-codec). A
  // plain word like "preview" must not be mistaken for one: a known product id
  // wins; otherwise only a token containing a digit is treated as a product.
  if (/^p[0-9a-z]+$/.test(segment)) {
    const id = decodeProductId(segment);
    const name = id !== null ? productNames?.get(id) : undefined;
    if (name) return name;
    if (/\d/.test(segment)) return 'Product';
  }
  const known = SEGMENT_LABELS[segment];
  if (known) return known;
  return segment
    .split('-')
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/** `/vault/document/<ref>` and its `/edit` + `/review` children. The ref is percent-encoded
 *  into the path ("attachment:260" → "attachment%3A260"), so the generic segment prettifier
 *  rendered it as "Attachment%3A260" — internal text on screen, on EVERY vault document page.
 *  A document is known by its name, so that is the crumb; the trailing verb hangs off it and
 *  the name links back to the document itself. */
const VAULT_DOC_RE = /^\/vault\/document\/([^/]+)(?:\/(edit|review))?\/?$/;
const VAULT_DOC_VERB: Record<string, string> = { edit: 'Editing', review: 'Review' };

export function crumbsForPath(
  pathname: string,
  search: Record<string, unknown> = {},
  productNames?: Map<number, string>,
  resultLeaf?: string,
  /** The open document's name, read from the cache the page already loaded (never fetched
   *  from chrome). Absent until it loads → the neutral "Document". */
  vaultDocName?: string,
  /** The edited company's name, read from the cache the wizard already loaded.
   *  Absent until it loads → the neutral "Company" (never the raw global id). */
  editedCompanyName?: string,
): Crumb[] {
  const dashboard = navItemForPath('/dashboard');
  const crumbs: Crumb[] = [
    { label: 'Dashboard', to: '/dashboard', icon: dashboard?.icon },
  ];

  // The company wizard's Edit reuses the create route — but ?id ALONE means nothing there
  // (creation also gains ?id once the first autosave persists the row), so
  // the Edit entry point declares itself with mode=edit.
  if (pathname === '/customer-account/new' && search['mode'] === 'edit') {
    const section = navItemForPath('/customer-account')!;
    crumbs.push({ label: section.label, to: section.to, icon: section.icon });
    const companyId = search['id'];
    if (typeof companyId === 'number' || typeof companyId === 'string') {
      crumbs.push({ label: editedCompanyName ?? 'Company', to: null });
    }
    crumbs.push({ label: 'Edit', to: null });
    return crumbs;
  }

  const result = RESULT_ROUTES[pathname];
  const item = navItemForPath(result ? result.section : pathname);
  if (result && item) {
    crumbs.push({ label: item.label, to: item.to, icon: item.icon });
    // The product name (resolved by the component from the loaded run) when known,
    // else the static leaf — so predicate's flat, product-less-capable result route
    // matches the product-in-path agents. Standalone runs have no name → static leaf.
    crumbs.push({ label: resultLeaf ?? result.leaf, to: null });
  } else if (item && VAULT_DOC_RE.test(pathname)) {
    const [, ref, verb] = pathname.match(VAULT_DOC_RE)!;
    crumbs.push({ label: item.label, to: item.to, icon: item.icon });
    crumbs.push({
      label: vaultDocName?.trim() || 'Document',
      // on /edit and /review the name is the way back to the document
      to: verb ? `/vault/document/${ref}` : null,
    });
    if (verb) crumbs.push({ label: VAULT_DOC_VERB[verb] ?? verb, to: null });
  } else if (item) {
    if (item.to !== '/dashboard') {
      crumbs.push({ label: item.label, to: item.to, icon: item.icon });
    }
    const rest = pathname.slice(item.to.length).split('/').filter(Boolean);
    let walked = item.to;
    for (const segment of rest) {
      walked = `${walked}/${segment}`;
      crumbs.push({
        label: prettify(segment, productNames),
        to: LINKABLE_SUBPATHS.has(walked) ? walked : null,
      });
    }
  } else if (MENU_ROUTE_LABELS[pathname]) {
    crumbs.push({ label: MENU_ROUTE_LABELS[pathname], to: null });
  } else {
    // Anything else off-nav: prettified segments, none of them links.
    for (const segment of pathname.split('/').filter(Boolean)) {
      crumbs.push({ label: prettify(segment, productNames), to: null });
    }
  }

  // Last crumb is the current page — never a link.
  const last = crumbs[crumbs.length - 1]!;
  crumbs[crumbs.length - 1] = { ...last, to: null };
  return crumbs;
}

export function BreadcrumbBar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({
    select: (s) => s.location.search as Record<string, unknown>,
  });
  // Resolve product-id crumbs to names off the shared ['products'] cache the
  // shell already loads (Subnav) — so the trail reads "HepaScreen…" instead of
  // the global "Product #12" that would leak cross-tenant volume.
  const { data: products } = useProducts();
  const productNames = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of products ?? []) {
      const name = p.product_name?.trim();
      if (name) m.set(p.id, name);
    }
    return m;
  }, [products]);
  // Predicate's result route (/predicate-finder-result) carries no product in its
  // URL — read the product name off the run the page already loaded so its
  // breadcrumb matches the other agents. Read-only (enabled:false): observe the
  // cache, never fetch from chrome; product-less standalone runs stay "Run Result".
  const predicateRunId =
    pathname === '/predicate-finder-result' && typeof search['run_id'] === 'number'
      ? (search['run_id'] as number)
      : null;
  const { data: predicateRun } = useQuery<AgentRunDetail>({
    queryKey: ['agent-run', predicateRunId],
    queryFn: () => getAgentRun(predicateRunId as number),
    enabled: false,
  });
  const resultLeaf = predicateRun?.product_name?.trim() || undefined;
  // The open vault document's name, observed off the cache its own page loaded — same
  // read-only posture as the predicate run above (enabled:false: chrome never fetches).
  const vaultDocRef = (() => {
    const m = pathname.match(VAULT_DOC_RE);
    return m?.[1] ? decodeURIComponent(m[1]) : null;
  })();
  const { data: vaultDoc } = useQuery<VaultDocumentDetail>({
    queryKey: ['vault', 'document', vaultDocRef],
    queryFn: () => getDocument(vaultDocRef as string),
    enabled: false,
  });
  const vaultDocName = vaultDoc?.product_name?.trim() || undefined;
  // The company open in the wizard, observed off the cache the wizard loaded —
  // same read-only posture as the two above (enabled:false: chrome never fetches).
  const editedCompanyId =
    pathname === '/customer-account/new' && typeof search['id'] === 'number'
      ? (search['id'] as number)
      : null;
  const { data: editedCompany } = useQuery<CompanyDto>({
    queryKey: ['companies', editedCompanyId],
    queryFn: () => getCompany(editedCompanyId as number),
    enabled: false,
  });
  const editedCompanyName = editedCompany ? companyName(editedCompany) : undefined;
  const crumbs = crumbsForPath(
    pathname,
    search,
    productNames,
    resultLeaf,
    vaultDocName,
    editedCompanyName,
  );

  return (
    <div className="bg-white border-b border-line">
      <nav
        aria-label="Breadcrumb"
        // Scrolls horizontally on narrow screens — hide the scrollbar and fade the right edge
        // so the overflow reads as a clean swipeable strip instead of a chopped-off word.
        className="px-4 sm:px-6 lg:px-10 py-2.5 flex items-center gap-1 text-[13px] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [mask-image:linear-gradient(to_right,#000_calc(100%-1.5rem),transparent)]"
      >
        {crumbs.map((crumb, i) => {
          const Icon = crumb.icon;
          return (
            <span key={`${crumb.label}-${i}`} className="flex items-center gap-1 whitespace-nowrap">
              {i > 0 && (
                <ChevronRight className="size-3 text-muted/40 shrink-0" aria-hidden="true" />
              )}
              {crumb.to ? (
                <Link
                  to={crumb.to}
                  className="flex items-center gap-1.5 rounded-md px-2 py-1 text-muted no-underline transition-colors duration-150 hover:text-navy hover:bg-[rgba(20,40,70,0.05)]"
                >
                  {Icon && <Icon className="size-3.5 shrink-0 opacity-70" aria-hidden="true" />}
                  {crumb.label}
                </Link>
              ) : (
                <span
                  className="flex items-center gap-1.5 rounded-full bg-[rgba(20,40,70,0.06)] px-2.5 py-1 text-[12.5px] font-semibold text-navy"
                  aria-current="page"
                >
                  {Icon && <Icon className="size-3.5 shrink-0" aria-hidden="true" />}
                  {crumb.label}
                </span>
              )}
            </span>
          );
        })}
      </nav>
    </div>
  );
}
